import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  IconButton,
  Box,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
// icons
import { Close } from "@mui/icons-material";
// config
import colors from "../../../config/colors";
// redux
import { useSelector } from "../../../redux/store";
import { useCreateWishlistMutation } from "../../../redux/apiSlices/wishlist";
// context
import { useAuth } from "../../../context/AuthContext";
// ga
import {
  triggerGa,
  CATEGORY,
  ACTION,
  PAGE,
  SCREEN,
  LABEL,
  LABEL2,
} from "../../../utils/gaUtils";

const CreateModal = ({
  open,
  onClose,
  onCreateWishlist = () => {},
  isAdmin = false,
  selectedBrandId = null,
}) => {
  const { user, selectedEmailUserBrand } = useAuth();
  const [wishlistName, setWishlistName] = useState("");
  const [wishlistDescription, setWishlistDescription] = useState("");
  const [nameError, setNameError] = useState("");
  const { wishlistCreatorId } = useSelector((state) => state.creator);

  const [createWishlist, { isLoading }] = useCreateWishlistMutation();

  const handleClose = (wishlistId) => {
    setWishlistName("");
    setWishlistDescription("");
    setNameError("");
    onClose(wishlistId);
  };

  const handleSubmit = () => {
    setNameError("");

    const body = {
      name: wishlistName,
      description: wishlistDescription,
      creator_ids: wishlistCreatorId ? [wishlistCreatorId] : [],
    };

    if (isAdmin && selectedBrandId) {
      // For admin users, pass admin_brand_id and set user_id to null
      body.admin_brand_id = selectedBrandId;
      body.user_id = null; // This will create wishlist with user_id = null
    } else {
      body.user_id = user?.data?.id;
      body.brand_id = selectedEmailUserBrand?.id;
    }

    createWishlist(body)
      .unwrap()
      .then((res) => {
        if (wishlistCreatorId) {
          triggerGa({
            category: CATEGORY.CREATOR_DISCOVERY,
            action: ACTION.BUTTON_CLICK,
            label: LABEL.ADD_TO_WISHLIST,
            label2: LABEL2.ADDED_TO_NEW,
            label3: wishlistCreatorId,
            page: PAGE.CREATORS,
            screenName: SCREEN.DISCOVER,
          });
          handleClose(res?.id);
        } else {
          handleClose();
        }
        enqueueSnackbar(`Wishlist created successfully!`, {
          variant: "success",
        });
        onCreateWishlist();
      })
      .catch((err) => {
        console.log(err);
        const errorMessage = err?.data?.error || err?.message || "";
        if (errorMessage.includes("already exists for this brand")) {
          setNameError("Name already exists, try a different name");
        } else {
          enqueueSnackbar(`Failed to create wishlist!`, {
            variant: "error",
          });
        }
      });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          padding: "24px",
          boxShadow: "0 2px 4px 2px rgba(0, 0, 0, 0.25)",
        },
      }}
    >
      <DialogTitle sx={{ p: 0, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography fontSize={20} fontWeight={600}>
            Create New Wishlist
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close sx={{ color: colors.black }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Stack spacing={2}>
          <Box>
            <Typography fontSize={14} mb={1}>
              Wishlist name
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter name here"
              value={wishlistName}
              onChange={(e) => {
                setWishlistName(e.target.value);
                if (nameError) setNameError("");
              }}
              error={!!nameError}
              helperText={nameError}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  "& fieldset": {
                    borderWidth: "1px",
                    borderColor: nameError ? colors.error500 : colors.gray100,
                  },
                  "&:hover fieldset": {
                    borderColor: nameError ? colors.error500 : colors.gray100,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: nameError
                      ? colors.error500
                      : colors.primary500,
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  color: colors.gray200,
                },
                "& .MuiFormHelperText-root": {
                  fontSize: "12px",
                  margin: "4px 0 0 0",
                },
              }}
            />
          </Box>

          <Box>
            <Typography fontSize={14} mb={1}>
              Wishlist description (optional)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Enter description here"
              value={wishlistDescription}
              onChange={(e) => setWishlistDescription(e.target.value)}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  "& fieldset": {
                    borderWidth: "1px",
                    borderColor: colors.gray100,
                  },
                  "&:hover fieldset": {
                    borderColor: colors.gray100,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.primary500,
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  color: colors.gray200,
                },
              }}
            />
          </Box>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading || !wishlistName}
            sx={{
              backgroundColor: colors.primary500,
              color: "white",
              display: "flex",
              padding: "12px",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              "&.Mui-disabled": {
                backgroundColor: colors.gray100,
                color: colors.gray200,
              },
            }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                Creating...
              </>
            ) : (
              "Create Wishlist"
            )}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default CreateModal;
